import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import {
  createAdminUser,
  createRole,
  deleteAdminUser,
  deleteUserRole,
  getAllAdminUser,
  getAllCaseManager,
  getAllPermissionConfig,
  getAllRolesConfig,
  updateAdminUser,
  updateRole,
  createPermission,
  updatePermission,
  updatePermissionOrder,
  deletePermission,
  updateEstablishment,
  getEstablishment,
  getUserSessions,
  approveSession,
  disapproveSession,
  revokeSession,
} from "./userAsyncThunk";
import { UserPermissionConfig, User, CreateUserRoleResponse, GetAllUserRolesResponse, AdminUser, Establishment, AdminSession } from "./types";
import { RequestResults } from "../../common/RequestStatus";

interface UsersState {
  data: AdminUser[];
  caseManagers: AdminUser[];
  allUserPermissions: UserPermissionConfig[];
  roles: GetAllUserRolesResponse[];
  counts: number;
  page: number;
  limit: number;
  status: RequestResults; // Fetching status
  loading: boolean; // Create/Update loading
  error: string | null;
  establishment: Establishment | null;
  sessions: AdminSession[];
  sessionsLoading: boolean;
}

const initialState: UsersState = {
  data: [],
  caseManagers: [],
  counts: 0,
  page: 1,
  limit: 10,
  allUserPermissions: [],
  roles: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
  establishment: null,
  sessions: [],
  sessionsLoading: false,
};

// Slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAdminUsers(state, action: PayloadAction<AdminUser[]>) {
      state.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch All user permission config
    builder
      .addCase(getAllPermissionConfig.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(getAllPermissionConfig.fulfilled, (state, action: PayloadAction<UserPermissionConfig[]>) => {
        state.allUserPermissions = action.payload;
        state.status = RequestResults.success;
      })
      .addCase(getAllPermissionConfig.rejected, (state, action) => {
        toast.error(action.payload || "Failed to get permissions");
        state.error = action.payload || "Failed to get permissions";
        state.status = RequestResults.failed;
      })

      // Create User Role
      .addCase(createRole.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action: PayloadAction<GetAllUserRolesResponse>) => {
        state.roles = [...state.roles, action.payload];
        state.status = RequestResults.success;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.error = action.payload || "Failed to create role";
        state.status = RequestResults.failed;
      })

      // Update User Role
      .addCase(updateRole.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action: PayloadAction<GetAllUserRolesResponse>) => {
        const index = state.roles.findIndex((role) => role.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        state.status = RequestResults.success;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.error = action.payload || "Failed to update role";
        state.status = RequestResults.failed;
      })

      // Get All Role
      .addCase(getAllRolesConfig.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(getAllRolesConfig.fulfilled, (state, action: PayloadAction<GetAllUserRolesResponse[]>) => {
        state.roles = action.payload;
        state.status = RequestResults.success;
      })
      .addCase(getAllRolesConfig.rejected, (state, action) => {
        toast.error(action.payload || "Failed to get roles");
        state.error = action.payload || "Failed to get roles";
        state.status = RequestResults.failed;
      })
      // Delete user role
      .addCase(deleteUserRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserRole.fulfilled, (state, action: any) => {
        state.roles = state.roles.filter((item) => item.id !== action.payload.id);
        state.loading = false;
      })
      .addCase(deleteUserRole.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete user role");
        state.error = action.payload || "Failed to delete user role";
      })

      // Create Admin User
      .addCase(createAdminUser.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(createAdminUser.fulfilled, (state, action: PayloadAction<AdminUser>) => {
        state.data = [action.payload, ...state.data];
        state.counts = state.counts + 1;
        state.loading = false;
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        // state.error = action.payload || 'Failed to create admin user';
        state.loading = false;
      })

      // Get All Admin Users
      .addCase(getAllAdminUser.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(getAllAdminUser.fulfilled, (state, action: PayloadAction<{ data: AdminUser[]; counts: number; limit: number }>) => {
        state.data = action.payload.data;
        state.counts = action.payload.counts;
        state.limit = action.payload.limit;
        state.status = RequestResults.success;
      })
      .addCase(getAllAdminUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to get admin users";
        state.status = RequestResults.failed;
      })

      // Update Admin User
      .addCase(updateAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminUser.fulfilled, (state, action: PayloadAction<AdminUser>) => {
        const index = state.data.findIndex((user) => user.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update admin user";
      })

      // Delete Admin User
      .addCase(deleteAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
        state.data = state.data.filter((user) => user.id !== action.payload.id);
        state.counts = state.counts - 1;
        state.loading = false;
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.loading = false;
        // state.error = action.payload || "Failed to delete admin user";
      })
      // Get All Case Manager
      .addCase(getAllCaseManager.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(getAllCaseManager.fulfilled, (state, action: PayloadAction<{ data: AdminUser[] }>) => {
        state.caseManagers = action.payload.data;
        state.status = RequestResults.success;
      })
      .addCase(getAllCaseManager.rejected, (state, action) => {
        state.error = action.payload || "Failed to get case managers";
        state.status = RequestResults.failed;
      })

      // Create Permission
      .addCase(createPermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPermission.fulfilled, (state, action: PayloadAction<UserPermissionConfig>) => {
        const groupedPermissions = state.allUserPermissions.reduce((acc: Record<string, UserPermissionConfig[]>, permission) => {
          const group = acc[permission.configType] || [];
          group.push(permission);
          acc[permission.configType] = group;
          return acc;
        }, {} as Record<string, UserPermissionConfig[]>);

        const MatchedGroup = groupedPermissions[action.payload.configType] || [];
        // MatchedGroup.push(action.payload);
        const newMatchedGroup = [action.payload, ...MatchedGroup];
        newMatchedGroup.sort((a, b) => a.index - b.index);
        newMatchedGroup.forEach((permission, index) => {
          permission.index = index;
        });
        groupedPermissions[action.payload.configType] = newMatchedGroup;

        state.allUserPermissions = Object.values(groupedPermissions).flat();
        state.loading = false;
        toast.success("Permission created successfully");
      })
      .addCase(createPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create permission";
        toast.error(action.payload || "Failed to create permission");
      })

      // Update Permission
      .addCase(updatePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePermission.fulfilled, (state, action: PayloadAction<UserPermissionConfig>) => {
        const index = state.allUserPermissions.findIndex((permission) => permission.id === action.payload.id);
        if (index !== -1) {
          state.allUserPermissions[index] = action.payload;
        }
        state.loading = false;
        toast.success("Permission updated successfully");
      })
      .addCase(updatePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update permission";
        toast.error(action.payload || "Failed to update permission");
      })

      // Update Permission Order
      .addCase(updatePermissionOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePermissionOrder.fulfilled, (state, action: PayloadAction<UserPermissionConfig[]>) => {
        state.allUserPermissions = action.payload;
        state.loading = false;
        toast.success("Permission order updated successfully");
      })
      .addCase(updatePermissionOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update permission order";
        toast.error(action.payload || "Failed to update permission order");
      })

      // Delete Permission
      .addCase(deletePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePermission.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
        const deletedPermission = state.allUserPermissions.find((permission) => permission.id === action.payload.id);
        if (!deletedPermission) return;
        const groupedPermissions = state.allUserPermissions.reduce((acc: Record<string, UserPermissionConfig[]>, permission) => {
          const group = acc[permission.configType] || [];
          group.push(permission);
          acc[permission.configType] = group;
          return acc;
        }, {} as Record<string, UserPermissionConfig[]>);
        const MatchedGroup = groupedPermissions[deletedPermission.configType] || [];

        const newMatchedGroup = MatchedGroup.filter((permission) => permission.id !== action.payload.id);
        newMatchedGroup.forEach((permission, index) => {
          permission.index = index;
        });
        groupedPermissions[deletedPermission.configType] = newMatchedGroup;
        state.allUserPermissions = Object.values(groupedPermissions).flat();

        state.loading = false;
        toast.success("Permission deleted successfully");
      })
      .addCase(deletePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete permission";
        toast.error(action.payload || "Failed to delete permission");
      })

      // Update Establishment
      .addCase(updateEstablishment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEstablishment.fulfilled, (state, action: PayloadAction<Establishment>) => {
        state.establishment = action.payload;
        state.loading = false;
        toast.success("Establishment updated successfully");
      })
      .addCase(updateEstablishment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update establishment";
        toast.error(action.payload || "Failed to update establishment");
      })

      //Get Establishment
      .addCase(getEstablishment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEstablishment.fulfilled, (state, action: PayloadAction<Establishment>) => {
        state.establishment = action.payload;
        state.loading = false;
      })
      .addCase(getEstablishment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch establishment";
        toast.error(action.payload || "Failed to fetch establishment");
      })

      // Get User Sessions
      .addCase(getUserSessions.pending, (state) => {
        state.sessionsLoading = true;
        state.error = null;
      })
      .addCase(getUserSessions.fulfilled, (state, action: PayloadAction<AdminSession[]>) => {
        state.sessions = action.payload;
        state.sessionsLoading = false;
      })
      .addCase(getUserSessions.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.error = action.payload || "Failed to fetch sessions";
        toast.error(action.payload || "Failed to fetch sessions");
      })

      // Approve Session
      .addCase(approveSession.pending, (state) => {
        state.sessionsLoading = true;
        state.error = null;
      })
      .addCase(approveSession.fulfilled, (state, action: PayloadAction<AdminSession>) => {
        const index = state.sessions.findIndex((session) => session.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        state.sessionsLoading = false;
        toast.success("Session approved successfully");
      })
      .addCase(approveSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.error = action.payload || "Failed to approve session";
        toast.error(action.payload || "Failed to approve session");
      })

      // Disapprove Session
      .addCase(disapproveSession.pending, (state) => {
        state.sessionsLoading = true;
        state.error = null;
      })
      .addCase(disapproveSession.fulfilled, (state, action: PayloadAction<AdminSession>) => {
        const index = state.sessions.findIndex((session) => session.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        state.sessionsLoading = false;
        toast.success("Session disapproved successfully");
      })
      .addCase(disapproveSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.error = action.payload || "Failed to disapprove session";
        toast.error(action.payload || "Failed to disapprove session");
      })

      // Revoke Session
      .addCase(revokeSession.pending, (state) => {
        state.sessionsLoading = true;
        state.error = null;
      })
      .addCase(revokeSession.fulfilled, (state, action: PayloadAction<{ sessionId: string }>) => {
        state.sessions = state.sessions.filter((session) => session.id !== action.payload.sessionId);
        state.sessionsLoading = false;
        toast.success("Session revoked successfully");
      })
      .addCase(revokeSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.error = action.payload || "Failed to revoke session";
        toast.error(action.payload || "Failed to revoke session");
      })
  },
});

export const { setAdminUsers } = userSlice.actions;

export const selectUsers = (state: RootState) => state.users;

export default userSlice.reducer;
